// RoleSelector.tsx - Toggle card role assignment component
import React from 'react';
import './RoleSelector.css';

export interface RoleOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  group?: string;        // For visual grouping (e.g., 'company', 'gate')
  alwaysOn?: boolean;    // Cannot be deselected (e.g., standard user)
}

interface RoleSelectorProps {
  roles: RoleOption[];
  selectedRoles: string[];
  onChange: (selectedRoles: string[]) => void;
  disabled?: boolean;
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
  roles,
  selectedRoles,
  onChange,
  disabled = false
}) => {
  const handleToggle = (roleId: string, alwaysOn?: boolean) => {
    if (disabled || alwaysOn) return;

    if (selectedRoles.includes(roleId)) {
      onChange(selectedRoles.filter(r => r !== roleId));
    } else {
      onChange([...selectedRoles, roleId]);
    }
  };

  // Group roles by their group property
  const groups: { [key: string]: RoleOption[] } = {};
  const ungrouped: RoleOption[] = [];

  roles.forEach(role => {
    if (role.group) {
      if (!groups[role.group]) groups[role.group] = [];
      groups[role.group].push(role);
    } else {
      ungrouped.push(role);
    }
  });

  const groupLabels: Record<string, string> = {
    'company-approval': 'Company approval workflow',
    'gate-approval': 'Gate / asset approval workflow',
    'gate-operations': 'Gate operations',
  };

  const renderCard = (role: RoleOption) => {
    const isActive = selectedRoles.includes(role.id);
    const isLocked = role.alwaysOn;

    return (
      <div
        key={role.id}
        className={`role-card ${isActive ? 'role-card-active' : ''} ${isLocked ? 'role-card-locked' : ''} ${disabled ? 'role-card-disabled' : ''}`}
        onClick={() => handleToggle(role.id, role.alwaysOn)}
        role="checkbox"
        aria-checked={isActive}
        aria-disabled={disabled || isLocked}
        tabIndex={disabled || isLocked ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleToggle(role.id, role.alwaysOn);
          }
        }}
      >
        <div className="role-card-content">
          <div className={`role-card-icon ${isActive ? 'role-icon-active' : ''}`}>
            {role.icon}
          </div>
          <div className="role-card-text">
            <div className="role-card-label">{role.label}</div>
            <div className="role-card-desc">{role.description}</div>
          </div>
        </div>
        <div className={`role-toggle ${isActive ? 'role-toggle-on' : ''} ${isLocked ? 'role-toggle-locked' : ''}`}>
          <div className="role-toggle-thumb" />
        </div>
      </div>
    );
  };

  return (
    <div className="role-selector">
      {/* Ungrouped roles first */}
      {ungrouped.map(renderCard)}

      {/* Grouped roles */}
      {Object.entries(groups).map(([groupKey, groupRoles]) => (
        <div key={groupKey} className="role-group">
          <div className="role-group-label">
            {groupLabels[groupKey] || groupKey}
          </div>
          {groupRoles.map(renderCard)}
        </div>
      ))}

      {/* Summary bar */}
      {selectedRoles.length > 0 && (
        <div className="role-summary-bar">
          <div className="role-summary-label">Assigned roles</div>
          <div className="role-summary-tags">
            {selectedRoles.map(roleId => {
              const role = roles.find(r => r.id === roleId);
              if (!role) return null;
              return (
                <span key={roleId} className="role-summary-tag">
                  {role.label}
                  {!role.alwaysOn && !disabled && (
                    <button
                      type="button"
                      className="role-tag-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(roleId);
                      }}
                      aria-label={`Remove ${role.label}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Predefined role configs ─────────────────────────────────────

const RoleIcons = {
  user: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  focalPoint: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  reviewer: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  approver: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  gate: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="9"/>
      <line x1="3" y1="21" x2="3" y2="12"/><line x1="21" y1="21" x2="21" y2="12"/>
      <line x1="10" y1="21" x2="10" y2="12"/><line x1="14" y1="21" x2="14" y2="12"/>
    </svg>
  ),
  supervisor: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  security: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  gateReviewer: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  gateApprover: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
};

export const COMPANY_ROLES: RoleOption[] = [
  {
    id: 'user',
    label: 'Standard user',
    description: 'Can submit gate pass requests',
    icon: RoleIcons.user,
    alwaysOn: true,
  },
  {
    id: 'focal_point',
    label: 'Focal point',
    description: 'Primary contact for the organisation',
    icon: RoleIcons.focalPoint,
  },
  {
    id: 'reviewer',
    label: 'Company reviewer',
    description: 'Reviews requests before company approval',
    icon: RoleIcons.reviewer,
    group: 'company-approval',
  },
  {
    id: 'approver',
    label: 'Company approver',
    description: 'Final company-level approval authority',
    icon: RoleIcons.approver,
    group: 'company-approval',
  },
  {
    id: 'gate_reviewer',
    label: 'Gate / asset reviewer',
    description: 'Reviews requests at assigned gates or assets',
    icon: RoleIcons.gateReviewer,
    group: 'gate-approval',
  },
  {
    id: 'gate_approver',
    label: 'Gate / asset approver',
    description: 'Final approval authority at assigned gates or assets',
    icon: RoleIcons.gateApprover,
    group: 'gate-approval',
  },
];

export const GATE_ROLES: RoleOption[] = [
  {
    id: 'gate_user',
    label: 'Gate user',
    description: 'Standard gate access and operations',
    icon: RoleIcons.gate,
    alwaysOn: true,
  },
  {
    id: 'gate_supervisor',
    label: 'Gate supervisor',
    description: 'Oversees gate personnel and operations',
    icon: RoleIcons.supervisor,
    group: 'gate-operations',
  },
  {
    id: 'security_officer',
    label: 'Security officer',
    description: 'Security-level approval authority',
    icon: RoleIcons.security,
    group: 'gate-operations',
  },
];

// ─── Utility: convert between roles array and comma-separated string ─
export function rolesToString(roles: string[]): string {
  return roles.filter(r => r).join(',');
}

export function stringToRoles(roleStr: string): string[] {
  if (!roleStr) return [];
  return roleStr.split(',').map(r => r.trim()).filter(r => r);
}